import { useEffect, useRef, useState } from "react";
import { OrdersViewModel } from "../../viewmodels/orders/OrdersViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import {
  convertStatus,
  formatDateWithTime,
  withRouter,
  isReadOnlyUser,
} from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import SimpleNotification from "../../components/SimpleNotification";

import { Link } from "react-router-dom";

import Pills from "../../components/controls/Pills";
import FilterDropdown from "../../components/filterDropdown/FilterDropdown";
import ButtonSpinner from "../../components/controls/ButtonSpinner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

function Orders(props) {
  const { logout } = props;
  const {
    emptyModal,
    getOrders,
    loading,
    orders,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    setShowSimpleNotification,
    showSimpleNotification,
    filters,
    showFilters,
    setShowFilters,
    filterComponents,
    resetFilters,
    getCustomers,
    getCarriersForDropdown,
    printShipments,
    totalOrders,
  } = OrdersViewModel(logout);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    getOrders(null, currentPage, filters);
    getCustomers();
    getCarriersForDropdown();
  }, []);

  const searchRef = useRef();

  const handleSearch = () => {
    getOrders(searchRef.current.value, 1, filters);
  };

  const handleLoadMoreData = async () => {
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await getOrders(searchRef.current.value, nextPage, filters);
    setLoadingMore(false);
  };

  function bodyDateFormatted(rowData) {
    return <div>{formatDateWithTime(rowData.created_date)}</div>;
  }

  function bodyStatus(rowData) {
    return <Pills content={convertStatus(rowData.status)} />;
  }

  const isReadOnly = isReadOnlyUser();

  function bodyMoreActions(rowData) {
    return (
      <Link
        className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        to={`/shipments/${rowData.id}`}
      >
        {isReadOnly ? "View" : "Edit"}
      </Link>
    );
  }

  return (
    <div>
      <SimpleNotification
        setShow={setShowSimpleNotification}
        showSimpleNotification={showSimpleNotification}
      />
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Shipments
              </h1>
              <div className="mt-5 block sm:flex rounded-md space-y-4 sm:space-y-0">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="search"
                    name="searchOrder"
                    id="searchOrder"
                    className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search by customer or po number"
                    ref={searchRef}
                    onKeyDown={(e) =>
                      e.key === "Enter" ? handleSearch() : null
                    }
                  />
                  <button
                    type="button"
                    className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                    onClick={() => handleSearch()}
                  >
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div className="flex">
                  <FilterDropdown
                    actionCall={() =>
                      getOrders(searchRef.current.value, 1, filters)
                    }
                    filterComponents={filterComponents}
                    resetFilters={() => {
                      resetFilters();
                    }}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                  />

                  <div>
                    <button
                      className="ml-2 rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
                      onClick={printShipments}
                    >
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <TableLoadingSkeleton rows={5} columns={4} />
                ) : (
                  <>
                    <div className="overflow-hidden card-custom">
                      <DataTable
                        value={orders}
                        dataKey="unique_id"
                        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                        cellClassName="p-2 border-1-bottom-gray text-sm"
                      >
                        <Column header="PO #" field="po_number" />
                        <Column header="Customer" field="customer_name" />
                        <Column header="Date" body={bodyDateFormatted} />
                        <Column header="Carrier" field="carrier_name" />
                        <Column header="Status" body={bodyStatus} />
                        <Column header="" body={bodyMoreActions} />
                      </DataTable>
                    </div>
                    {orders.length < totalOrders ? (
                      <div className="mt-4 text-center">
                        <button
                          onClick={handleLoadMoreData}
                          disabled={loadingMore}
                          className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ButtonSpinner spin={loadingMore} text="Load More" />
                        </button>
                      </div>
                    ) : (
                      <p className="mt-4 text-center text-gray-500">
                        No more shipments to load
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Orders));
